import { useState, type FormEvent } from 'react';
import axios from 'axios';
import { api } from '../api/client';
import type { SubmitEntryResponse } from '../api/types';

interface Props {
  /** Wenn vorhanden, wird die Puzzle-Nr beim Submit aus dem Share-Text
   *  gegen diese Nummer geprüft (Konsistenz). */
  expectedPuzzleNumber?: number;
  /** Vorgabe-Datum für das Auto-Create-Popup (Default: heute).
   *  Wird beim Backfill-Flow auf den ausgewählten Tag gesetzt. */
  defaultPuzzleDate?: string;
  /** Spieltag, für den eingetragen wird. Wird als `for_date`
   *  mitgeschickt, damit der Server bei Wiederholungs-Nummern die
   *  richtige Rätsel-Instanz trifft (statt einer alten gleichen Nummer). */
  entryDate?: string;
  onSubmitted(response: SubmitEntryResponse): void;
}

interface PuzzleNotFoundState {
  puzzle_number: number;
}

interface PuzzleCreateFields {
  puzzle_date: string;
  solution_year: string;
  hint_1: string;
  hint_2: string;
  hint_3: string;
  hint_4: string;
}

export function EntryForm({ expectedPuzzleNumber, defaultPuzzleDate, entryDate, onSubmitted }: Props) {
  const [shareText, setShareText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [needsDate, setNeedsDate] = useState<PuzzleNotFoundState | null>(null);

  const submit = async (
    event: FormEvent<HTMLFormElement> | null,
    extra?: Partial<PuzzleCreateFields>,
  ) => {
    event?.preventDefault();
    setError(null);

    const trimmed = shareText.trim();
    if (!trimmed) {
      setError('Bitte den Share-Text aus der Pastpuzzle-App einfügen');
      return;
    }
    if (expectedPuzzleNumber != null) {
      const headerMatch = /pastpuzzle\s+(\d+)/i.exec(trimmed);
      const found = headerMatch ? Number.parseInt(headerMatch[1] ?? '', 10) : NaN;
      if (Number.isFinite(found) && found !== expectedPuzzleNumber) {
        setError(
          `Der Share-Text gehört zu Rätsel #${found}, heute läuft aber #${expectedPuzzleNumber}.`,
        );
        return;
      }
    }

    const body: Record<string, unknown> = { raw_share_text: trimmed };
    // Wenn das Tagesrätsel bereits in der DB liegt, geben wir die
    // Nummer immer explizit mit — so funktionieren auch Sonder-Rätsel
    // mit nicht-numerischem Header (z.B. "#pastpuzzle ME/CFS Awareness").
    if (expectedPuzzleNumber != null) body.puzzle_number = expectedPuzzleNumber;
    // Spieltag-Kontext für die datums-genaue Auflösung (Wiederholungs-
    // Nummern). Wird vom Anlege-Datum `puzzle_date` überstimmt.
    if (entryDate) body.for_date = entryDate;
    if (extra?.puzzle_date) body.puzzle_date = extra.puzzle_date;
    if (extra) {
      const solution = parseOptionalInt(extra.solution_year);
      if (solution !== undefined) body.solution_year = solution;
      const h1 = extra.hint_1?.trim();
      const h2 = extra.hint_2?.trim();
      const h3 = extra.hint_3?.trim();
      const h4 = extra.hint_4?.trim();
      if (h1) body.hint_1 = h1;
      if (h2) body.hint_2 = h2;
      if (h3) body.hint_3 = h3;
      if (h4) body.hint_4 = h4;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post<SubmitEntryResponse>('/entries', body);
      setNeedsDate(null);
      onSubmitted(data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data) {
        const data = err.response.data as {
          error?: string;
          detail?: string;
          code?: string;
          puzzle_number?: number;
        };
        if (
          err.response.status === 404 &&
          data.code === 'PUZZLE_NOT_FOUND' &&
          typeof data.puzzle_number === 'number'
        ) {
          // Erst-Anleger-Flow: Server kennt das Rätsel noch nicht.
          // Popup mit Datum + optional Lösung + Tipps öffnen.
          setNeedsDate({ puzzle_number: data.puzzle_number });
          return;
        }
        setError(data.detail ? `${data.error}: ${data.detail}` : data.error ?? 'Submit fehlgeschlagen');
      } else {
        setError('Submit fehlgeschlagen');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form className="entry-form" onSubmit={submit}>
        <label className="field">
          <span className="field__label">Pastpuzzle-Ergebnis</span>
          <textarea
            className="field__input field__input--textarea"
            rows={9}
            placeholder={'#pastpuzzle 477\n🟩🟩🟥🟥 (+4)\n🟩🟥🟩🟥 (-109)\n…\nx/4 🟥'}
            value={shareText}
            onChange={(e) => setShareText(e.target.value)}
            required
          />
        </label>

        {error && <p className="entry-form__error" role="alert">{error}</p>}

        <button
          type="submit"
          className="button button--primary"
          disabled={submitting}
        >
          {submitting ? 'Wird gesendet …' : 'Ergebnis abschicken'}
        </button>
      </form>

      {needsDate && (
        <PuzzleCreateDialog
          state={needsDate}
          defaultDate={defaultPuzzleDate ?? todayIso()}
          onCancel={() => setNeedsDate(null)}
          onConfirm={(fields) => void submit(null, fields)}
          submitting={submitting}
        />
      )}
    </>
  );
}

interface DialogProps {
  state: PuzzleNotFoundState;
  defaultDate: string;
  submitting: boolean;
  onConfirm(fields: PuzzleCreateFields): void;
  onCancel(): void;
}

function PuzzleCreateDialog({ state, defaultDate, submitting, onConfirm, onCancel }: DialogProps) {
  const [fields, setFields] = useState<PuzzleCreateFields>({
    puzzle_date: defaultDate,
    solution_year: '',
    hint_1: '',
    hint_2: '',
    hint_3: '',
    hint_4: '',
  });

  const update = <K extends keyof PuzzleCreateFields>(key: K, value: PuzzleCreateFields[K]) =>
    setFields((s) => ({ ...s, [key]: value }));

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="puzzle-dialog-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel modal__panel--scroll">
        <h2 id="puzzle-dialog-title" className="modal__title">
          Rätsel #{state.puzzle_number} ist neu
        </h2>
        <p className="modal__text">
          Datum bestätigen (heute ist voreingestellt). Lösung und Tipps
          kannst du gleich mitanlegen oder später im Admin nachtragen.
        </p>

        <label className="field">
          <span className="field__label">Datum</span>
          <input
            className="field__input"
            type="date"
            value={fields.puzzle_date}
            onChange={(e) => update('puzzle_date', e.target.value)}
            max={offsetIso(+1)}
            autoFocus
          />
        </label>
        <p className="modal__hint">
          Vergangene Daten sind erlaubt (Backfill); maximal +1 Tag in
          die Zukunft (Zeitzonen-Toleranz).
        </p>

        <label className="field">
          <span className="field__label">Lösung (Jahr, optional)</span>
          <input
            className="field__input"
            type="number"
            inputMode="numeric"
            placeholder="z.B. 1972"
            value={fields.solution_year}
            onChange={(e) => update('solution_year', e.target.value)}
          />
        </label>

        {([1, 2, 3, 4] as const).map((n) => (
          <label key={n} className="field">
            <span className="field__label">Hinweis {n} (optional)</span>
            <textarea
              className="field__input field__input--textarea admin__hint-input"
              rows={3}
              placeholder="Mehrzeiliger Hinweis möglich"
              value={fields[`hint_${n}` as const]}
              onChange={(e) => update(`hint_${n}` as const, e.target.value)}
            />
          </label>
        ))}

        <div className="modal__buttons">
          <button
            type="button"
            className="button button--ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={() => onConfirm(fields)}
            disabled={submitting || !fields.puzzle_date}
          >
            {submitting ? 'Lege an …' : 'Anlegen & abschicken'}
          </button>
        </div>
      </div>
    </div>
  );
}

function parseOptionalInt(raw: string | undefined): number | undefined {
  if (raw == null) return undefined;
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function todayIso(): string {
  return offsetIso(0);
}

function offsetIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
